import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { config } from 'services/config';
import { SettingsContext } from '@/state/contexts';
import { useLazyGetChatMessagesQuery } from '@/state/slices/copilot/copilotChats.ts';
import { CopilotWsClient, WsStatus } from './ws/client';
import { useVoiceCapture } from './audio/useVoiceCapture';
import {
  CopilotContext,
  CopilotContextValue,
  CopilotMessage,
} from './CopilotContext';
import { newId, toCopilotMessage } from './messageState';
import { useMessageHandler } from './useMessageHandler';
import {
  isPopoutWindow,
  publishActiveStudy,
  readInitialPopoutStudy,
  subscribeActiveStudy,
} from './popout/popoutChannel';

export function CopilotProvider({ children }: { children: ReactNode }) {
  const { settings } = useContext(SettingsContext);
  const enabled = Boolean(settings.copilotEnabled && config.COPILOT_WS_URL);

  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<WsStatus>('closed');
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [conversationUuid, setConversationUuid] = useState<string>(newId);
  const [activeStudyUuid, setActiveStudyUuid] = useState<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  // True from the instant Send is pressed until the first token/tool/done, so
  // the typing indicator covers the pre-stream gap (Gemini thinking + tools)
  // before any pending assistant bubble exists.
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [pendingTranscript, setPendingTranscript] = useState<string | null>(
    null
  );

  const clientRef = useRef<CopilotWsClient | null>(null);
  const lastUserTextRef = useRef<string>('');
  const activeHistoryUuidRef = useRef<string | null>(null);
  const [fetchMessages] = useLazyGetChatMessagesQuery();

  const { start: startCapture, stop: stopCapture } = useVoiceCapture({
    onInterim: (t) => setInterimTranscript(t),
    onFinal: (t) => {
      setInterimTranscript('');
      setPendingTranscript(t);
    },
    onError: () => setIsRecording(false),
  });

  const { handleMessage, streamingIdRef } = useMessageHandler({
    setMessages,
    setAwaitingReply,
    setInterimTranscript,
    setPendingTranscript,
  });

  useEffect(() => {
    if (!enabled || !isOpen) return undefined;
    const token = localStorage.getItem('AccessToken');
    if (!token) return undefined;
    const client = new CopilotWsClient({
      baseUrl: config.COPILOT_WS_URL,
      conversationUuid,
      token,
      onMessage: handleMessage,
      onStatus: setStatus,
    });
    clientRef.current = client;
    client.connect();
    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [enabled, isOpen, conversationUuid, handleMessage]);

  // Never leave the typing indicator or mic stuck if the socket drops mid-turn.
  useEffect(() => {
    if (status === 'closed' || status === 'error') {
      setAwaitingReply(false);
      setIsRecording(false);
      stopCapture();
    }
  }, [status, stopCapture]);

  // Bridge the open study across the popout boundary. The popout window has no
  // router (no CopilotStudySync), so without this its messages carry no study
  // context and the copilot says "no study open". The popout learns the study
  // from its URL on open + the main window's broadcasts; the main window
  // broadcasts whenever its active study changes.
  useEffect(() => {
    if (isPopoutWindow()) return undefined;
    publishActiveStudy(activeStudyUuid);
    return undefined;
  }, [activeStudyUuid]);

  useEffect(() => {
    if (!isPopoutWindow()) return undefined;
    const initial = readInitialPopoutStudy();
    if (initial) setActiveStudyUuid(initial);
    return subscribeActiveStudy(setActiveStudyUuid);
  }, [setActiveStudyUuid]);

  const sendText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !clientRef.current) return;
      lastUserTextRef.current = trimmed;
      streamingIdRef.current = null;
      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'user',
          text: trimmed,
          toolCalls: [],
          pending: false,
        },
      ]);
      setAwaitingReply(true);
      clientRef.current.sendUserMessage(
        trimmed,
        activeStudyUuid ? { study_uuid: activeStudyUuid } : undefined
      );
    },
    [activeStudyUuid, streamingIdRef]
  );

  const retryLastTurn = useCallback(() => {
    if (lastUserTextRef.current) sendText(lastUserTextRef.current);
  }, [sendText]);

  const loadHistory = useCallback(
    (uuid: string) => {
      setIsLoadingMessages(true);
      setAwaitingReply(false);
      activeHistoryUuidRef.current = uuid;
      fetchMessages({ uuid })
        .unwrap()
        .then((res) => {
          // Discard a stale response if the user switched chats meanwhile.
          if (activeHistoryUuidRef.current !== uuid) return;
          setMessages(
            res.messages
              .map(toCopilotMessage)
              .filter((m): m is CopilotMessage => m !== null)
          );
        })
        .catch(() => {
          if (activeHistoryUuidRef.current === uuid) setMessages([]);
        })
        .finally(() => {
          if (activeHistoryUuidRef.current === uuid)
            setIsLoadingMessages(false);
        });
    },
    [fetchMessages]
  );

  const switchConversation = useCallback(
    (uuid: string) => {
      if (uuid === conversationUuid) return;
      streamingIdRef.current = null;
      setAwaitingReply(false);
      setMessages([]);
      setConversationUuid(uuid);
      loadHistory(uuid);
    },
    [conversationUuid, loadHistory, streamingIdRef]
  );

  const createNewChat = useCallback(() => {
    streamingIdRef.current = null;
    setAwaitingReply(false);
    // Invalidate any in-flight history load so it can't clobber the new chat.
    activeHistoryUuidRef.current = null;
    setMessages([]);
    setIsLoadingMessages(false);
    setConversationUuid(newId());
  }, [streamingIdRef]);

  const setRecording = useCallback(
    (recording: boolean) => {
      setIsRecording(recording);
      if (recording) {
        setInterimTranscript('');
        startCapture();
      } else {
        stopCapture();
      }
    },
    [startCapture, stopCapture]
  );

  const clearPendingTranscript = useCallback(
    () => setPendingTranscript(null),
    []
  );

  const value = useMemo<CopilotContextValue>(
    () => ({
      enabled,
      conversationUuid,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((v) => !v),
      messages,
      awaitingReply,
      sendText,
      sendVoiceChunk: (b64: string) => clientRef.current?.sendVoiceChunk(b64),
      endVoice: () => clientRef.current?.endVoice(),
      isRecording,
      setRecording,
      interimTranscript,
      pendingTranscript,
      clearPendingTranscript,
      retryLastTurn,
      status,
      activeStudyUuid,
      setActiveStudyUuid,
      isLoadingMessages,
      switchConversation,
      createNewChat,
    }),
    [
      enabled,
      conversationUuid,
      isOpen,
      messages,
      awaitingReply,
      sendText,
      retryLastTurn,
      status,
      activeStudyUuid,
      isLoadingMessages,
      switchConversation,
      createNewChat,
      isRecording,
      setRecording,
      interimTranscript,
      pendingTranscript,
      clearPendingTranscript,
    ]
  );

  return (
    <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>
  );
}
